/* The page after the purchase (sections/tm-confirmation.liquid) and the basket snapshot that feeds it.
 *
 * Shopify's own thank you page cannot be replaced without Plus, and since 26 August 2026 a non Plus store
 * cannot run a script on it either: Additional scripts is gone from Settings > Checkout and a custom pixel
 * runs in a sandboxed frame that cannot navigate the page it sits on. So the order has to reach
 * /pages/confirmation through the browser, and this file reads it from whichever of the three sources the
 * store actually has, best first:
 *
 *   1. ?order= in the URL, put there by the redirect the owner installs
 *   2. localStorage "tm-confirmation-order", written by that redirect or by a custom pixel
 *   3. localStorage "tm-confirmation-basket", written here from the checkout screen, so the tickets still
 *      list when nothing was installed at all
 *
 * Both stored records use one shape, amounts as decimal strings in the shop's currency:
 *   { number, currency, total, statusUrl, lines: [{ title, variant, quantity, price, linePrice }] }
 *
 * THE REDIRECT. It is not automatic and no theme file can make it so. Where a store still has the box at
 * Settings > Checkout > Order status page > Additional scripts, this goes in it verbatim:
 *
 *   <script>
 *     (function () {
 *       var c = window.Shopify && window.Shopify.checkout;
 *       if (!c) return;
 *       try {
 *         localStorage.setItem("tm-confirmation-order", JSON.stringify({
 *           number: c.order_number,
 *           currency: c.currency,
 *           total: c.total_price,
 *           statusUrl: c.order_status_url,
 *           lines: (c.line_items || []).map(function (l) {
 *             return {
 *               title: l.title,
 *               variant: l.variant_title,
 *               quantity: l.quantity,
 *               price: l.price,
 *               linePrice: l.line_price
 *             };
 *           })
 *         }));
 *       } catch (e) {}
 *       window.location.replace("/pages/confirmation?order=" + encodeURIComponent(c.order_number));
 *     })();
 *   </script>
 *
 * Where that box is gone, a custom pixel at Settings > Customer events can still write the record but not
 * redirect, so the buyer reaches the page by a link. Either way the page renders without it. */
(function () {
  "use strict";

  var ORDER_KEY = "tm-confirmation-order";
  var BASKET_KEY = "tm-confirmation-basket";

  function readStore(name) {
    try {
      return window.localStorage.getItem(name);
    } catch (e) {
      /* A private window can refuse to read, in which case the page falls back to the next source. */
      return null;
    }
  }

  function writeStore(name, value) {
    try {
      window.localStorage.setItem(name, value);
    } catch (e) {
      /* Nothing to do: the snapshot is a convenience, the confirmation email is the record. */
    }
  }

  function readRecord(name) {
    var raw = readStore(name);
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  /* The checkout screen, sections/tm-cart.liquid. The basket is read from Shopify rather than from the
   * rendered rows, so the snapshot carries the same numbers the checkout will charge. A quantity typed
   * into a row and not applied with Update is not in the basket yet, so it is not in the snapshot either. */
  var checkoutForm = document.querySelector(".tm-checkout__form");
  if (checkoutForm) {
    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (response) {
        return response && response.ok ? response.json() : null;
      })
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) return;
        writeStore(
          BASKET_KEY,
          JSON.stringify({
            currency: cart.currency,
            /* cart.js counts in the currency's minor unit, the stored shape is decimal. */
            total: cart.total_price,
            lines: cart.items.map(function (item) {
              return {
                title: item.product_title,
                variant: item.variant_title,
                quantity: item.quantity,
                price: item.price,
                linePrice: item.final_line_price
              };
            })
          })
        );
      })
      .catch(function () {
        /* An offline basket is not worth an error on the checkout screen. */
      });
  }

  var page = document.querySelector("[data-tm-confirmation]");
  if (!page) return;

  var order = readRecord(ORDER_KEY);
  var basket = readRecord(BASKET_KEY);

  var reference = "";
  try {
    reference = new URLSearchParams(window.location.search).get("order") || "";
  } catch (e) {
    reference = "";
  }
  if (!reference && order && order.number) reference = String(order.number);

  /* A record left by an earlier order must not be dressed up as this one: when the URL names an order the
   * stored record does not, only the number from the URL is trusted and the lines are left to the basket. */
  var record = null;
  if (order && (!reference || !order.number || String(order.number) === reference)) {
    record = order;
  } else if (basket) {
    record = basket;
  }

  var currency = (record && record.currency) || "";

  /* Everything reaching here is in the shop's minor unit: Shopify hands cents both in the order record the
   * status page writes and in /cart.js, so the divide belongs here, once, rather than at each caller. */
  function money(amount) {
    var value = parseFloat(amount);
    if (isNaN(value)) return "";
    value = value / 100;
    if (currency) {
      try {
        return new Intl.NumberFormat(undefined, { style: "currency", currency: currency }).format(value);
      } catch (e) {
        /* An unknown currency code falls through to the plain number rather than throwing the page away. */
      }
    }
    return value.toFixed(2);
  }

  function show(element) {
    if (element) element.hidden = false;
  }

  var referenceRow = page.querySelector("[data-tm-confirmation-reference]");
  var referenceValue = page.querySelector("[data-tm-confirmation-order]");
  /* Shopify's sequence starts at 1001 and cannot be moved, so the reference is offset before it is shown.
   * The record in the admin keeps its real number, and the email adds the same offset. */
  function withOffset(value) {
    var raw = String(value).replace(/^#/, "");
    if (!/^\d+$/.test(raw)) return String(value);
    var offset = parseInt(referenceRow && referenceRow.getAttribute("data-number-offset"), 10);
    if (isNaN(offset)) offset = 0;
    return String(parseInt(raw, 10) + offset);
  }

  if (reference && referenceValue) {
    referenceValue.textContent = "#" + withOffset(reference);
    show(referenceRow);
  }

  var list = page.querySelector("[data-tm-confirmation-lines]");
  var lines = (record && record.lines) || [];
  if (list && lines.length) {
    for (var i = 0; i < lines.length; i += 1) {
      var line = lines[i];
      var item = document.createElement("li");
      item.className = "tm-checkout__line";

      var text = document.createElement("div");
      text.className = "tm-checkout__line-text";

      var name = document.createElement("span");
      name.className = "tm-checkout__line-name";
      name.textContent = line.title || "";
      text.appendChild(name);

      /* A single variant extra has Shopify's placeholder title, which is not worth a line. */
      if (line.variant && line.variant !== "Default Title") {
        var variant = document.createElement("span");
        variant.className = "tm-checkout__line-variant";
        variant.textContent = line.variant;
        text.appendChild(variant);
      }

      var unit = document.createElement("span");
      unit.className = "tm-checkout__line-unit";
      var quantity = parseInt(line.quantity, 10);
      if (isNaN(quantity) || quantity < 1) quantity = 1;
      unit.textContent = quantity > 1 ? quantity + " x " + money(line.price) : money(line.price) + " each";
      text.appendChild(unit);

      var side = document.createElement("div");
      side.className = "tm-checkout__line-side";
      var total = document.createElement("span");
      total.className = "tm-checkout__line-total";
      total.textContent = money(line.linePrice);
      side.appendChild(total);

      item.appendChild(text);
      item.appendChild(side);
      list.appendChild(item);
    }
    show(list);
  }

  var totalRow = page.querySelector("[data-tm-confirmation-total-row]");
  var totalValue = page.querySelector("[data-tm-confirmation-total]");
  if (record && record.total && totalValue) {
    totalValue.textContent = money(record.total);
    show(totalRow);
  }

  /* Shopify's order status page is the record of the order, so the link goes there when its URL travelled
   * with the redirect. Without it the page offers no link it cannot honour. */
  var status = page.querySelector("[data-tm-confirmation-status]");
  if (status && record && record.statusUrl) {
    status.setAttribute("href", record.statusUrl);
    show(status);
  }

  if (!reference && !lines.length) {
    show(page.querySelector("[data-tm-confirmation-note]"));
  }
})();
